// src/components/dashboard/AccountBalanceCard.jsx
import React, { useState } from "react";
import { showToast } from "../../utils/helpers";
import AccountDetailsCard from "./AccountDetailsCard";
import ClaimBalanceModal from "../modals/ClaimBalanceModal";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import ConnectBankModal from "../modals/ConnectBankModal";

const AccountBalanceCard = ({
  user,
  accountDetails,
  existingAccount,
  dashboardData,
  onDataRefresh,
}) => {
  const [isBalanceHidden, setIsBalanceHidden] = useState(
    localStorage.getItem("hideBalance") === "true"
  );
  const [showInfoToast, setShowInfoToast] = useState(true);
  const [showConnectBankModal, setShowConnectBankModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const navigate = useNavigate();

  const toggleBalanceVisibility = () => {
    const newState = !isBalanceHidden;
    setIsBalanceHidden(newState);
    localStorage.setItem("hideBalance", newState.toString());
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showToast("Account number copied to clipboard!", "success");
      })
      .catch(() => {
        showToast("Failed to copy account number", "error");
      });
  };

  const shareAccountDetails = () => {
    const shareText = `My ${accountDetails?.bank_name} Account Details:
Account Name: ${accountDetails?.account_name}
Account Number: ${accountDetails?.account_number}
Bank: ${accountDetails?.bank_name}`;

    if (navigator.share) {
      navigator
        .share({
          title: "My Account Details",
          text: shareText,
        })
        .catch((error) => {
          console.log("Error sharing:", error);
          copyToClipboard(shareText);
          showToast("Account details copied to clipboard!", "success");
        });
    } else {
      copyToClipboard(shareText);
      showToast("Account details copied to clipboard!", "success");
    }
  };

  const shareViaWhatsApp = () => {
    const shareText = `My ${accountDetails?.bank_name} Account Details:%0AAccount Name: ${accountDetails?.account_name}%0AAccount Number: ${accountDetails?.account_number}%0ABank: ${accountDetails?.bank_name}`;
    window.open(`https://wa.me/?text=${shareText}`, "_blank");
  };

  const shareViaEmail = () => {
    const subject = "My Account Details";
    const body = `My ${accountDetails?.bank_name} Account Details:\nAccount Name: ${accountDetails?.account_name}\nAccount Number: ${accountDetails?.account_number}\nBank: ${accountDetails?.bank_name}`;
    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
        body
      )}`,
      "_blank"
    );
  };

  const handleRedirect = (url) => {
    navigate(url);
  };

  const formatAmount = (amount) => {
    return new Number(amount || 0).toLocaleString();
  };

  const hiddenText = "******";

  // ✅ Check if pre-registration balance is available to claim
  const hasPreRegBalance = dashboardData?.PreRegBalance > 0;

  // ✅ Handle successful account connection
  const handleAccountConnected = () => {
    if (onDataRefresh) {
      onDataRefresh();
    }
  };

  // ✅ Handle successful claim
  const handleClaimSuccess = () => {
    setIsClaiming(false);
    setShowClaimModal(false);
    showToast("Pre-registration balance claimed successfully!", "success");

    if (onDataRefresh) {
      onDataRefresh();
    }
  };

  // ✅ Handle claim error
  const handleClaimError = (error) => {
    setIsClaiming(false);
    showToast(error.message || "Failed to claim balance", "error");
  };

  const [showBalances, setShowBalances] = useState(false);

  return (
    <div className="w-full mx-auto flex flex-col lg:flex-row gap-4 lg:gap-8 py-4 px-3">
      {/* Left - Balance and Actions Card */}
<div className="w-full bg-[#1e5aff] rounded-[25px] shadow-md border border-blue-200 p-5 flex flex-col h-fit relative overflow-hidden">
        
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e5aff] via-[#0f4fff] to-[#0044cc] opacity-95 rounded-[25px] -z-0"></div>
        <div className="relative z-10">
          {/* Verification Badge */}
          {user?.is_verified === "yes" ? (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs font-semibold mb-4 border border-blue-300 shadow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                fill="currentColor"
                className="mr-1"
                viewBox="0 0 22 16"
              >
                <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01zm.287 5.984-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7 8.793l2.646-2.647a.5.5 0 0 1 .708.708" />
              </svg>
              VERIFIED BUSINESS
            </div>
          ) : null}

     
       {/* Account Details */}
<div className="space-y-2 text-xs font-semibold mb-4">
  <div className="flex items-center gap-2 text-white">
    
    {/* Information Toast - Inside Container */}
    {showInfoToast && (
    <div className="w-full overflow-hidden rounded-xl bg-gray-800/95 shadow-lg backdrop-blur-sm border border-gray-700">
      <div className="relative flex items-center">
        {/* Icon Container */}
        <div className="flex-shrink-0 pl-2 pr-1 py-1.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20">
            <span className="text-xs font-bold text-blue-400">!</span>
          </div>
        </div>
        
        {/* Marquee Text Container */}
        <div className="flex-1 overflow-hidden py-1.5">
          <div className="animate-marquee whitespace-nowrap text-[11px] text-gray-300">
            <span className="inline-block px-3">
              Due to regulatory requirements, we no longer provide dedicated virtual account numbers for wallet Top-Ups.
              You can still setup a withdrawal account with the button below. Billing will be handled from the billing tab under your profile settings.
              We apologise for any inconvenience this may cause.
            </span>
            {/* Duplicate for seamless loop */}
            <span className="inline-block px-3">
              Due to regulatory requirements, we no longer provide dedicated virtual account numbers for wallet Top-Ups.
              You can still setup a withdrawal account with the button below. Billing will be handled from the billing tab under your profile settings.
              We apologise for any inconvenience this may cause.
            </span>
          </div>
        </div>
        
        {/* Close Button */}
        <button
          className="flex-shrink-0 pr-2 text-gray-500 hover:text-gray-300 transition-colors"
          onClick={() => setShowInfoToast(false)}
          aria-label="Dismiss information notice"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    )}

    {/* Your original account details code (commented out) */}
    {/* <span>Acc. No: {accountDetails?.account_number || "N/A"}</span>
    {accountDetails?.account_number && (
      <button ...>...</button>
    )} */}
  </div>
  {/* <div className="font-medium text-sm text-white">
    {accountDetails?.bank_name || "N/A"}
  </div> */}
</div>

     {/* Balance */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl font-semibold text-white">
                {accountDetails?.currency || "NGN"}
              </span>
              <span className="text-3xl font-bold text-white">
                {isBalanceHidden
                  ? hiddenText
                  : formatAmount(accountDetails?.balance)}
              </span>
              <button
                className="p-1 text-white hover:text-gray-200 transition-colors"
                onClick={toggleBalanceVisibility}
                title="Toggle Balance"
              >
                {isBalanceHidden ? (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.064 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    ></path>
                  </svg>
                )}
              </button>
            </div>
            <div className="text-sm text-gray-200">
           Connect your bank account to receive your funds.
              </div>
            <button
              onClick={() => handleRedirect("/transactions")}
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-white bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg px-3 py-2 transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Transaction History
            </button>
          </div>

          {/* Action Buttons */}
          {/* <div className="flex gap-6 justify-center mb-4">
            <div className="flex flex-col items-center text-center">
              <button
                className="w-12 h-12 flex items-center justify-center bg-white text-[#1e5aff] rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => setShowConnectBankModal(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 14 14"
                >
                  <path
                    fill="#1e5aff"
                    fillRule="evenodd"
                    d="M7 .066a1.21 1.21 0 0 0-.634.18l-5.91 3.87H.455c-.42.277-.547.754-.385 1.161c.156.392.55.657 1.02.657h11.82c.47 0 .864-.265 1.02-.657c.162-.407.034-.884-.385-1.16l-.001-.001l-5.91-3.87A1.21 1.21 0 0 0 7 .066M0 11.877c0-.6.572-.889 1-.889h.055a.504.504 0 0 1-.003-.054V7.723a.5.5 0 0 1 .5-.5h1.664a.5.5 0 0 1 .5.5v3.211a.507.507 0 0 1-.003.054h1.958a.507.507 0 0 1-.003-.054V7.723a.5.5 0 0 1 .5-.5h1.664a.5.5 0 0 1 .5.5v3.211a.504.504 0 0 1-.003.054h1.958a.507.507 0 0 1-.003-.054V7.723a.5.5 0 0 1 .5-.5h1.664a.5.5 0 0 1 .5.5v3.211a.507.507 0 0 1-.003.054H13c.428 0 1 .289 1 .89v1.167c0 .6-.572.889-1 .889H1c-.428 0-1-.289-1-.89z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <span className="text-xs mt-1 text-white">Connect Bank</span>
            </div>

            <div className="flex flex-col items-center text-center">
              <button
                className="w-12 h-12 flex items-center justify-center bg-white text-[#1e5aff] rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => handleRedirect("/transactions")}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.0509 0H4.24759C0.845918 0 0 0.908911 0 4.53556V14.6686C0 17.0623 1.31387 17.6293 2.90672 15.9195L2.91572 15.9105C3.65364 15.1275 4.77854 15.1905 5.41747 16.0454L6.32638 17.2603C7.05531 18.2232 8.2342 18.2232 8.96313 17.2603L9.87204 16.0454C10.52 15.1815 11.6449 15.1185 12.3828 15.9105C13.9846 17.6203 15.2895 17.0533 15.2895 14.6596V4.53556C15.2985 0.908911 14.4526 0 11.0509 0ZM10.124 7.87423H5.1745C4.80553 7.87423 4.49956 7.56826 4.49956 7.1993C4.49956 6.83034 4.80553 6.52436 5.1745 6.52436H10.124C10.493 6.52436 10.7989 6.83034 10.7989 7.1993C10.7989 7.56826 10.493 7.87423 10.124 7.87423Z"
                    fill="#1e5aff"
                  />
                </svg>
              </button>
              <span className="text-xs mt-1 text-white">Transactions</span>
            </div>
          </div> */}


        </div>
        {/* Right - Bank Account Details Card */}
        <div className="w-full lg:w-1/2 h-fit hidden">
          <AccountDetailsCard
            user={user}
            accountDetails={accountDetails}
            shareViaEmail={shareViaEmail}
            shareViaWhatsApp={shareViaWhatsApp}
            shareAccountDetails={shareAccountDetails}
          />
        </div>
        {/* Connect Bank Account Modal */}
        <ConnectBankModal
          show={showConnectBankModal}
          onHide={() => setShowConnectBankModal(false)}
          onAccountConnected={handleAccountConnected}
        />
        {/* ✅ Claim Balance Modal */}{" "}
        <ClaimBalanceModal
          show={showClaimModal}
          onHide={() => setShowClaimModal(false)}
          balance={dashboardData?.PreRegBalance}
          currency={accountDetails?.currency || "NGN"}
          onClaimSuccess={handleClaimSuccess}
          onClaimError={handleClaimError}
        />
      </div>
    </div>
  );
};

export default AccountBalanceCard;
